import { type MedalKind, type MedalLandscape, medalLandscapeOrdinal } from "@healthos/client-data";
import { MEDAL_FINISHES, MEDAL_INLAY, SCENIC_MEDAL_FINISHES } from "@healthos/tokens";
import { useId } from "react";
import { FINISHER_OUTLINE, FINISHER_WINDOW, FinisherArtwork } from "./finisher-artwork";
import { landscapeImage } from "./medal-assets";
import { STREAK_FINISHES, StreakMedalArtwork } from "./streak-medal-artwork";

// Artwork materials are intentionally independent of the surrounding UI theme.
// These finishes match the bundled GLB masters.
export { MEDAL_FINISHES } from "@healthos/tokens";

export function MedalArtwork({
  kind,
  size = 140,
  subdued = false,
  ordinal = 0,
  landscape,
}: {
  kind: MedalKind;
  size?: number;
  subdued?: boolean;
  ordinal?: number;
  landscape?: MedalLandscape;
}) {
  const id = useId().replace(/:/g, "");
  const landscapeOrdinal = medalLandscapeOrdinal(landscape);
  const landscapeMetal =
    landscapeOrdinal === 3 || landscapeOrdinal === 4
      ? SCENIC_MEDAL_FINISHES.bronze
      : landscapeOrdinal === 5
        ? SCENIC_MEDAL_FINISHES.warm
        : {};
  const finish = subdued
    ? SCENIC_MEDAL_FINISHES.unearned
    : {
        ...(kind === "streak"
          ? (STREAK_FINISHES[Math.max(0, Math.min(3, ordinal - 1))] ?? STREAK_FINISHES[0])
          : MEDAL_FINISHES[kind]),
        ...landscapeMetal,
      };
  const inlay = subdued ? MEDAL_INLAY.unearned : MEDAL_INLAY.metal;
  const inlayLight = subdued ? finish.light : MEDAL_INLAY.light;
  const shape =
    kind === "streak"
      ? "streak"
      : kind === "completion"
        ? "crest"
        : landscape
          ? landscapeOrdinal === 1
            ? "hex"
            : landscapeOrdinal === 3 || landscapeOrdinal === 4
              ? "shield"
              : "round"
          : kind === "waypoint"
            ? "hex"
            : "shield";
  const outline =
    shape === "streak"
      ? "M100 19 L148 42 L168 96 L148 150 L100 173 L52 150 L32 96 L52 42 Z"
      : shape === "crest"
        ? FINISHER_OUTLINE
        : shape === "hex"
          ? "M100 20 L165 58 L165 134 L100 172 L35 134 L35 58 Z"
          : shape === "round"
            ? "M100 18 C144 18 174 52 174 95 C174 138 144 172 100 172 C56 172 26 138 26 95 C26 52 56 18 100 18 Z"
            : "M42 28 L158 28 L165 112 Q150 151 100 175 Q50 151 35 112 Z";
  return (
    <svg
      width={size}
      height={size}
      style={{ maxWidth: "100%", height: "auto" }}
      viewBox="0 0 200 200"
      aria-hidden="true"
    >
      <defs>
        <clipPath id={`${id}inlay`}>
          <path d={shape === "crest" ? FINISHER_WINDOW : outline} />
        </clipPath>
        <linearGradient id={`${id}metal`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={finish.light} />
          <stop offset="0.3" stopColor={finish.metal} />
          <stop offset="0.56" stopColor={finish.light} />
          <stop offset="1" stopColor={finish.metal} />
        </linearGradient>
        <linearGradient id={`${id}satin`} x1="0" y1="0" x2="0.5" y2="1">
          <stop offset="0" stopColor={finish.light} />
          <stop offset="0.45" stopColor={finish.metal} />
          <stop offset="1" stopColor={finish.metal} />
        </linearGradient>
        <linearGradient id={`${id}teal`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={inlayLight} />
          <stop offset="0.4" stopColor={inlay} />
          <stop offset="1" stopColor={inlay} />
        </linearGradient>
        <linearGradient id={`${id}enamel`} x1="0" y1="0" x2="0.8" y2="1">
          <stop offset="0" stopColor={finish.enamel} />
          <stop offset="1" stopColor={finish.shadow} />
        </linearGradient>
      </defs>
      <ellipse cx="100" cy="183" rx="53" ry="7" fill={finish.shadow} opacity={0.13} />
      <path d={outline} transform="translate(0 5)" fill={finish.shadow} />
      <path d={outline} fill={`url(#${id}metal)`} stroke={finish.metal} strokeWidth="1" />
      <path
        d={outline}
        transform="translate(100 96) scale(0.96) translate(-100 -96)"
        fill={`url(#${id}satin)`}
        stroke={finish.light}
        strokeWidth="0.6"
      />
      <g transform="translate(100 96) scale(0.87) translate(-100 -96)">
        <path
          d={shape === "crest" ? FINISHER_WINDOW : outline}
          fill={`url(#${id}enamel)`}
          stroke={finish.shadow}
          strokeWidth="2"
        />
        <path
          d={shape === "crest" ? FINISHER_WINDOW : outline}
          fill="none"
          stroke={finish.light}
          strokeWidth="1"
          opacity={0.4}
        />
      </g>
      <path
        d={shape === "crest" ? FINISHER_WINDOW : outline}
        transform="translate(100 96) scale(0.84) translate(-100 -96)"
        fill="none"
        stroke={`url(#${id}teal)`}
        strokeWidth="1.8"
      />
      {kind === "streak" ? (
        <StreakMedalArtwork level={ordinal} light={finish.light} enamel={finish.shadow} />
      ) : landscape ? (
        <g transform="translate(100 96) scale(0.82) translate(-100 -96)">
          <g clipPath={`url(#${id}inlay)`}>
            <image
              href={landscapeImage(landscape, subdued)}
              x={23}
              y={18}
              width={154}
              height={154}
              preserveAspectRatio="xMidYMid slice"
              opacity={subdued ? 0.65 : 1}
            />
            {subdued ? (
              <path
                d={shape === "crest" ? FINISHER_WINDOW : outline}
                fill={finish.shadow}
                opacity={0.15}
              />
            ) : null}
          </g>
          <path
            d={shape === "crest" ? FINISHER_WINDOW : outline}
            fill="none"
            stroke={finish.light}
            strokeWidth={1.5}
            opacity={0.8}
          />
        </g>
      ) : kind === "waypoint" ? (
        <g
          fill="none"
          stroke={finish.light}
          strokeWidth="5"
          strokeLinejoin="round"
          strokeLinecap="round"
        >
          <path d="M59 114 L85 76 L104 99 L122 69 L145 114 Z" />
          <path d="M76 91 L85 97 L92 86 M113 84 L123 92 L130 83" strokeWidth="2" />
          <circle cx="68" cy="66" r="5" fill={finish.metal} stroke="none" />
        </g>
      ) : kind === "completion" ? (
        <g
          fill="none"
          stroke={finish.light}
          strokeWidth="5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M71 133 C47 105 82 110 81 91 C81 75 109 94 111 75 L111 56" />
          <path d="M112 57 L137 63 L112 73 Z" fill={finish.metal} strokeWidth="2" />
          <circle cx="71" cy="133" r="5" fill={finish.light} />
          <path
            d="M46 101 Q46 132 72 146 M154 101 Q154 132 128 146"
            strokeWidth="2"
            opacity={0.6}
          />
        </g>
      ) : (
        <g
          fill="none"
          stroke={finish.light}
          strokeWidth="5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M76 65 L124 65 L120 92 Q100 113 80 92 Z" />
          <path d="M76 71 L63 71 Q61 94 81 94 M124 71 L137 71 Q139 94 119 94 M100 105 L100 127 M85 130 L115 130" />
        </g>
      )}
      {ordinal > 0 && kind === "waypoint" && !landscape ? (
        <text
          x="100"
          y="142"
          textAnchor="middle"
          fontSize="13"
          fontWeight="600"
          fill={finish.light}
          letterSpacing="3"
        >
          {String(ordinal).padStart(2, "0")}
        </text>
      ) : null}
      {kind === "completion" ? (
        <FinisherArtwork metal={finish.metal} light={finish.light} enamel={finish.shadow} />
      ) : null}
      {landscape && kind !== "completion"
        ? Array.from(
            { length: ordinal || 7 },
            (_, index) => 100 + (index - ((ordinal || 7) - 1) / 2) * 5,
          ).map((x) => (
            <circle key={x} cx={x} cy={shape === "hex" ? 161 : 160} r={1.3} fill={finish.light} />
          ))
        : null}
    </svg>
  );
}
